
export interface TermsSection {
    id: string;
    title: string;
    content: JSX.Element;
}

export interface TermsData {
    title: string;
    sections: TermsSection[];
}

export const TermsData: TermsData = {
    title: "WAIVERA Terms of Service",

    sections: [
        {
            id: "terms",
            title: "WAIVERA Terms of Service",
            content: (
                <>
                    <p>
                        Welcome to WAIVERA, an electronic signature platform developed by Waivera, Inc
                        (“Company,” “we,” “us,” or “our”). WAIVERA is designed to facilitate secure and
                        legally binding electronic signatures for Medicaid case managers, waiver clients, in-
                        home providers, caretakers, and state representatives.
                    </p>
                    <p>
                        By using WAIVERA, you agree to these Terms of Service (“Terms”), which govern your
                        access to and use of the platform. These Terms form a legally binding agreement
                        between you and Waivera, Inc. If you do not agree to these Terms, you may not use
                        WAIVERA.
                    </p>
                    <p>
                        WAIVERA complies with HIPAA, Medicaid regulations, the ESIGN Act, UETA, and other
                        applicable federal and state laws (including Louisiana and Florida regulations). The
                        platform ensures that all electronic signatures executed through it are legally
                        recognized and securely stored.
                    </p>
                    <p>
                        These Terms apply whether you access WAIVERA via the Apple App Store, Google
                        Play Store, or through other authorized channels. Please read them carefully before
                        using the platform.
                    </p>
                </>
            ),
        },
        {
            id: "acceptance",
            title: "Acceptance of Terms",
            content: (
                <>
                    <p>
                        By accessing or using WAIVERA, you acknowledge that you have read, understood,
                        and agree to be bound by these Terms of Service and our Privacy Policy. If you do
                        not agree to these Terms, you must immediately discontinue using WAIVERA and any
                        related services.
                    </p>
                    <p>
                        Your continued use of WAIVERA, including accessing the platform via the Apple App
                        Store or Google Play Store, constitutes your acceptance of these Terms and all
                        applicable laws and regulations. If at any time you no longer agree with any part of
                        these Terms, you must cease all use of the platform.
                    </p>
                </>
            ),
        },
        {
            id: "definitions",
            title: "Definitions",
            content: (
                <>
                    <p>For the purposes of these Terms, the following definitions apply:</p>
                    <ul className="list-disc ml-6 space-y-2">
                        <li><strong>“WAIVERA” –</strong> The electronic signature platform developed by Waivera, Inc, designed to
                            facilitate secure, legally binding electronic signatures for Medicaid case managers,
                            waiver clients, in-home providers, caretakers, and state representatives.</li>
                        <li><strong>“Company” / “We” / “Us” / “Our” –</strong> Refers to Waivera, Inc, the owner and operator of
                            WAIVERA.</li>
                        <li><strong>“User” / “You” / “Your” –</strong> Any individual or entity that accesses or uses WAIVERA,
                            including but not limited to Medicaid case managers, waiver clients, in-home
                            providers, caretakers, and state representatives.</li>
                        <li><strong>“Electronic Signature” –</strong> A legally binding electronic mark, symbol, or process that
                            a user applies to a document to indicate consent or approval, in accordance with
                            the ESIGN Act (U.S.) and UETA.</li>
                        <li><strong>“Platform” –</strong> The WAIVERA application, website, and related services, including those
                            available via the Apple App Store and Google Play Store.</li>
                        <li><strong>“Account” –</strong> A registered user profile created on WAIVERA that allows access to the
                            platform’s services.</li>
                        <li><strong>“Personal Data” –</strong> Any information that identifies or relates to an individual,
                            including but not limited to names, contact details, electronic signature records,
                            and Protected Health Information (PHI).</li>
                        <li><strong>“Protected Health Information (PHI)” –</strong> Health-related data that is subject to the
                            Health Insurance Portability and Accountability Act (HIPAA) and is used, stored, or
                            transmitted within WAIVERA.</li>
                        <li><strong>“Business Associate Agreement (BAA)” –</strong> A contract between Waivera, Inc and covered
                            entities ensuring compliance with HIPAA regarding the handling of PHI.</li>
                        <li><strong>“Audit Trail” –</strong> A secure record detailing who signed a document, when, and where,
                            ensuring compliance with regulatory requirements.</li>
                        <li><strong>“Third-Party Services” –</strong> Any external services, providers, or platforms that integrate
                            with WAIVERA, including cloud storage and authentication tools.</li>
                        <li><strong>“Apple App Store” and “Google Play Store” –</strong> Digital distribution platforms where
                            users can download and install WAIVERA. Use of WAIVERA via these platforms is
                            subject to their respective terms and policies.</li>
                        <li><strong>“Medicaid Regulations” –</strong> Federal and state-specific laws governing Medicaid
                            services, including those applicable in Louisiana and Florida.</li>
                    </ul>
                </>
            ),
        },
        {
            id: "eligibility",
            title: "Eligibility",
            content: (
                <>
                    <div className="text-gray-700 space-y-4 text-sm sm:text-base">
                        <h3 className="font-bold text-gray-800 text-lg">4.1 Who Can Use WAIVERA</h3>
                        <p>
                            WAIVERA is designed exclusively for individuals and entities involved in Medicaid
                            services, including Medicaid case managers, waiver clients, in-home providers,
                            caretakers, and state representatives. Access to the platform is restricted to users
                            who have a legitimate need to execute, manage, or oversee electronic signatures
                            related to Medicaid and healthcare services.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">4.2 Authorization to Sign Electronically</h3>
                        <p>
                            To use WAIVERA, you must have the legal authority to execute electronic signatures
                            on behalf of yourself, your organization, or any entity you represent. By accessing
                            the platform, you confirm that you are duly authorized to sign electronically and
                            that any signature executed through WAIVERA will be considered legally binding
                            under the ESIGN Act (U.S.) and the Uniform Electronic Transactions Act (UETA).
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">4.3 Compliance with Laws and Regulations</h3>
                        <p>
                            Users of WAIVERA must ensure that their use of the platform complies with all
                            applicable federal and state laws, Medicaid regulations, and HIPAA requirements.
                            WAIVERA is currently designed for operations within Louisiana and Florida, and users
                            must adhere to the specific legal and regulatory frameworks governing electronic
                            transactions and healthcare data within these jurisdictions.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">4.4 Restrictions on Use</h3>
                        <p>
                            WAIVERA is strictly for professional and authorized use. Any unauthorized,
                            fraudulent, or unlawful use of the platform is prohibited. If you do not meet the
                            eligibility criteria outlined in this section, you are not permitted to access or use
                            WAIVERA, and any attempt to do so may result in the termination of your account.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: "useofPlatform",
            title: "Use of the Platform",
            content: (
                <>
                    <div className="text-gray-700 space-y-4 text-sm sm:text-base">
                        <h3 className="font-bold text-gray-800 text-lg">5.1 Agreement to Privacy and Security Terms</h3>
                        <p>
                            Before accessing or using WAIVERA, all users must review and accept the Privacy
                            Policy and Security & Compliance Terms. These policies govern how personal data,
                            including Protected Health Information (PHI), is collected, processed, and stored.
                            By proceeding with account creation or any transaction within WAIVERA, you
                            acknowledge that you have read, understood, and agreed to these terms. Failure to
                            accept these policies prohibits access to the platform.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">5.2 Electronic Signature Consent</h3>
                        <p>
                            WAIVERA facilitates legally binding electronic signatures in compliance with the
                            ESIGN Act and UETA. Before initiating or completing an electronic signature request,
                            users must provide explicit consent to use electronic signatures. This consent
                            affirms that all signatures executed through WAIVERA will carry the same legal
                            weight as handwritten signatures. Users who do not agree to electronic signatures
                            must discontinue using the platform, as WAIVERA operates exclusively within a digital
                            signature framework.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">5.3 Secure Document Management</h3>
                        <p>
                            All documents signed through WAIVERA are securely processed and stored within
                            the platform. The system employs encryption, audit logs, and controlled access
                            mechanisms to ensure document integrity, prevent unauthorized modifications,
                            and comply with HIPAA and other data protection regulations. Once a document is
                            fully executed, WAIVERA retains a permanent, tamper-proof record, accessible only
                            to authorized parties. Additionally, an audit trail is generated for each document,
                            detailing who signed, when, and where, ensuring transparency and compliance
                            with regulatory standards.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: "electronicSignatures",
            title: "Electronic Signatures",
            content: (
                <>
                    <div className="text-gray-700 space-y-4 text-sm sm:text-base">
                        <h3 className="font-bold text-gray-800 text-lg">6.1 Legally Binding Digital Signatures</h3>
                        <p>
                            WAIVERA facilitates the execution of legally binding electronic signatures in full
                            compliance with the Electronic Signatures in Global and National Commerce Act
                            (ESIGN Act) and the Uniform Electronic Transactions Act (UETA). By using WAIVERA,
                            you acknowledge and agree that any electronic signature you provide through the
                            platform is legally valid, enforceable, and carries the same effect as a handwritten
                            signature under applicable federal and state laws.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">6.2 Equivalence to Handwritten Signatures</h3>
                        <p>
                            Electronic signatures executed through WAIVERA are recognized as legally binding
                            and equivalent to handwritten signatures. This means that any agreement,
                            contract, or consent form signed using WAIVERA is legally enforceable and admissible
                            in a court of law. You waive any right to challenge the validity or enforceability of a
                            document based on the fact that it was signed electronically rather than with a
                            physical signature.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">6.3 User Responsibility and Consent</h3>
                        <p>
                            By electronically signing a document through WAIVERA, you confirm that:
                            <ul className="list-disc">
                                <li>You have the authority to sign on behalf of yourself or your organization.</li>
                                <li>You understand that your electronic signature has the same legal force as a
                                    handwritten signature.</li>
                                <li>You consent to the storage and retention of your signed documents in
                                    compliance with HIPAA, Medicaid regulations, and applicable state laws.</li>
                            </ul>
                            If you do not agree to the use of electronic signatures, you must immediately
                            discontinue use of WAIVERA, as the platform operates exclusively in a digital
                            signature environment.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: "complianceWithLaws",
            title: "Compliance with Laws",
            content: (
                <>
                    <div className="text-gray-700 space-y-4 text-sm sm:text-base">
                        <h3 className="font-bold text-gray-800 text-lg">7.1 Adherence to Legal and Regulatory Standards</h3>
                        <p>
                            WAIVERA operates within a highly regulated environment, ensuring full compliance
                            with all applicable federal, state, and industry-specific laws, including but not
                            limited to:
                            <ul className="list-disc">
                                <li>The Health Insurance Portability and Accountability Act (HIPAA), which
                                    mandates the protection of Protected Health Information (PHI).</li>
                                <li>Medicaid regulations, governing the handling and execution of digital
                                    transactions within state-funded healthcare programs.</li>
                                <li>The Electronic Signatures in Global and National Commerce Act (ESIGN Act) and
                                    the Uniform Electronic Transactions Act (UETA), ensuring the validity of
                                    electronic signatures.</li>
                                <li>State-specific regulations, particularly those governing electronic transactions,
                                    data privacy, and Medicaid compliance in Louisiana and Florida.</li>
                            </ul>
                            By using WAIVERA, you agree to abide by these laws and regulations at all times.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">7.2 User Responsibility for Compliance</h3>
                        <p>
                            Users are solely responsible for ensuring their actions on WAIVERA comply with all
                            applicable laws, professional standards, and regulatory requirements relevant to
                            their role. This includes but is not limited to:
                            <ul className="list-disc">
                                <li>Ensuring that any electronic signature transactions conducted through WAIVERA
                                    comply with HIPAA, Medicaid policies, and applicable state laws.</li>
                                <li>Properly handling and safeguarding patient information and PHI in accordance
                                    with HIPAA and other data privacy laws.</li>
                                <li>Obtaining and verifying the necessary authorizations and consents before
                                    signing or facilitating the execution of documents.</li>
                            </ul>
                            Failure to comply with these regulations may result in immediate suspension or
                            termination of access to WAIVERA, as well as potential legal and regulatory
                            consequences.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">7.3 No Assumption of Liability by WAIVERA</h3>
                        <p>
                            While WAIVERA provides a legally compliant platform for electronic signatures, Waivera,
                            Inc. does not assume responsibility for any misuse, unauthorized access, or non-
                            compliance by users. Each user is expected to exercise due diligence, uphold all
                            applicable laws, and take full responsibility for their digital transactions on the
                            platform.
                        </p>
                        <p>
                            If you are uncertain about compliance obligations related to your specific use of
                            WAIVERA, you should seek legal or regulatory guidance before proceeding.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: "appStore",
            title: "App Store and Google Play Store Compliance",
            content: (
                <>
                    <div className="text-gray-700 space-y-4 text-sm sm:text-base">
                        <h3 className="font-bold text-gray-800 text-lg">8.1 Availability on App Stores:</h3>
                        <p>
                            WAIVERA is available for download and use on Apple App Store and Google Play Store. By accessing WAIVERA through these platforms, you acknowledge and agree to comply with the respective store’s terms, policies, and usage guidelines.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">8.2 Compliance with App Store and Play Store Terms:</h3>
                        <p>
                            Users must adhere to all applicable terms and conditions set by Apple App Store and Google Play Store, including but not limited to:
                            <ul className="list-disc">
                                <li>Policies governing app usage, data privacy, and security.</li>
                                <li>Restrictions on unauthorized modifications, reverse engineering, or prohibited
                                    activities.</li>
                                <li>Compliance with billing, subscription, and refund policies if applicable.</li>
                            </ul>
                            Failure to comply with these terms may result in suspension of access to WAIVERA
                            or restrictions imposed by the respective app store.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">8.3 Waivera, Inc.’s Responsibility for Functionality and Compliance:</h3>
                        <p>
                            Waivera, Inc. is solely responsible for the development, functionality, and legal compliance of WAIVERA. The availability of WAIVERA on third-party platforms does not create any affiliation, endorsement, or liability on the part of Apple Inc. or Google LLC. Any disputes, issues, or concerns related to WAIVERA must be directed to Waivera, Inc. through our official support channels.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">8.4 App Updates and Maintenance:</h3>
                        <p>
                            To ensure continued functionality, security, and compliance with evolving regulations, users must install all WAIVERA updates as they become available on the Apple App Store or Google Play Store. Failure to install updates may result in performance issues, loss of access to certain features, or potential non-compliance with regulatory changes.
                            Waivera, Inc. reserves the right to modify, update, or discontinue certain features of WAIVERA to maintain security, performance, and compliance with applicable laws. By continuing to use WAIVERA, you acknowledge your responsibility to keep the application up to date and comply with all relevant app store policies.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: "userAccounts",
            title: "User Accounts",
            content: (
                <>
                    <div className="text-gray-700 space-y-4 text-sm sm:text-base">
                        <h3 className="font-bold text-gray-800 text-lg">9.1 Account Registration and Accuracy of Information:</h3>
                        <p>
                            To access and use WAIVERA, users must create an account by providing accurate, complete, and up-to-date information during registration. You agree that all details provided, including your name, contact information, and professional credentials, are truthful and that you will promptly update any changes to ensure ongoing accuracy. Providing false or misleading information may result in the suspension or termination of your account.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">9.2 Responsibility for Account Security:</h3>
                        <p>
                            Users are solely responsible for maintaining the confidentiality of their login credentials, including usernames, passwords, and authentication methods. You agree to:
                            <ul className="list-disc">
                                <li>Keep your login details secure and not share them with any third party.</li>
                                <li>Take reasonable precautions to prevent unauthorized access to your account.</li>
                                <li>Immediately notify Waivera, Inc. if you suspect any unauthorized use of your
                                    account or security breaches related to WAIVERA.</li>
                            </ul>
                            Waivera, Inc. is not liable for any loss, damage, or unauthorized transactions resulting
                            from your failure to secure your account credentials.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">9.3 Prohibition of Unauthorized Access:</h3>
                        <p>
                            Users may only access their own accounts and are strictly prohibited from:
                            <ul className="list-disc">
                                <li>Using another person’s WAIVERA account without proper authorization.</li>
                                <li>Attempting to hack, bypass, or manipulate security measures to gain
                                    unauthorized access.</li>
                                <li>Engaging in fraudulent or unlawful activities using WAIVERA.</li>
                            </ul>
                            Any violation of these terms may lead to immediate account suspension or
                            termination, along with potential legal action if unauthorized access results in
                            security breaches or data compromise.
                        </p>
                        <p>
                            By creating and maintaining a WAIVERA account, you acknowledge and accept these
                            responsibilities to ensure a secure, compliant, and professional digital environment.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: "pataProtection",
            title: "Data Protection and Security",
            content: (
                <>
                    <div className="text-gray-700 space-y-4 text-sm sm:text-base">
                        <h3 className="font-bold text-gray-800 text-lg">10.1 Secure Cloud Storage:</h3>
                        <p>
                            WAIVERA leverages Microsoft Azure for secure cloud
                            storage, ensuring that all user data, including electronic signature records and
                            protected health information (PHI), is stored in a highly secure, compliant, and
                            scalable environment. Azure provides advanced security protocols, including data
                            encryption, redundant backups, and restricted access controls, to safeguard
                            sensitive information.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">10.2 Authentication and Identity Verification:</h3>
                        <p>
                            User authentication and identity
                            verification within WAIVERA are managed through Google Firebase, a trusted and
                            industry-leading authentication platform. Firebase ensures secure login, two-factor
                            authentication (if enabled), and real-time identity verification to prevent
                            unauthorized access and enhance platform security.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">10.3 No Third-Party Data Processors:</h3>
                        <p>
                            Waivera, Inc. does not share, sell, or transfer user
                            data to third-party processors for storage or processing. All data collected and
                            managed within WAIVERA remains securely housed within Microsoft Azure and
                            Google Firebase infrastructure, ensuring compliance with HIPAA, Medicaid
                            regulations, and applicable state laws.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">10.4 Security Measures:</h3>
                        <p>
                            To protect user data and maintain regulatory compliance,
                            WAIVERA implements industry-standard security measures, including but not limited
                            to:
                            <ul className="list-disc">
                                <li>Encryption: All stored and transmitted data is encrypted using AES-256
                                    encryption and SSL/TLS protocols to prevent unauthorized interception or
                                    exposure.</li>
                                <li>Access Controls: Role-based access control (RBAC) ensures that only authorized
                                    personnel can view or manage sensitive information.</li>
                                <li>Audit Logs: A comprehensive audit trail is maintained for all transactions,
                                    tracking who accessed, modified, or signed documents, ensuring transparency
                                    and regulatory adherence.</li>
                            </ul>
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">10.5 User Responsibility for Data Security:</h3>
                        <p>
                            While WAIVERA provides a secure and
                            compliant infrastructure, users are responsible for protecting their login
                            credentials, safeguarding access to their accounts, and reporting any suspicious
                            activity. Failure to maintain proper security practices may compromise data
                            integrity, for which Waivera, Inc. assumes no liability.
                        </p>
                        <p>
                            By using WAIVERA, you acknowledge and agree to these data protection and security
                            measures, ensuring the platform remains a trusted, compliant, and secure
                            environment for electronic transactions.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: "limitationOfLiability",
            title: "Limitation of Liability",
            content: (
                <>
                    <div className="text-gray-700 space-y-4 text-sm sm:text-base">
                        <h3 className="font-bold text-gray-800 text-lg">11.1 No Liability for Unauthorized Access or Data Loss:</h3>
                        <p>
                            Waivera, Inc. takes extensive security measures to protect user data; however, we do not guarantee absolute
                            security and shall not be held liable for any unauthorized access, data breaches, or
                            loss of information resulting from factors beyond our control. This includes, but is
                            not limited to, cyberattacks, security vulnerabilities, third-party system failures, or
                            user negligence in safeguarding login credentials.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">11.2 Platform Use at Your Own Risk:</h3>
                        <p>
                            By using WAIVERA, you acknowledge and agree
                            that you do so at your own risk. Waivera, Inc. provides the platform on an “as-is” and
                            “as-available” basis without any warranties, express or implied, regarding its
                            performance, reliability, availability, or suitability for specific purposes. We do not
                            guarantee that the platform will be error-free, uninterrupted, or completely secure
                            at all times.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">11.3 Exclusion of Indirect and Consequential Damages:</h3>
                        <p>
                            To the fullest extent
                            permitted by law, Waivera, Inc. shall not be liable for any indirect, incidental,
                            consequential, special, or punitive damages, including but not limited to:
                            <ul className="list-disc">
                                <li>Loss of data, revenue, or business opportunities.</li>
                                <li>Service disruptions, application downtime, or inability to access WAIVERA.</li>
                                <li>Legal or regulatory consequences arising from improper use of the platform.</li>
                            </ul>
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">11.4 Maximum Liability:</h3>
                        <p>
                            In no event shall Waivera, Inc.’s total liability, whether in
                            contract, tort, or otherwise, exceed the total amount paid by the user (if any) for
                            access to WAIVERA in the six (6) months preceding the claim. If no fees have been
                            paid, Waivera, Inc.'s liability shall be strictly limited to $100 USD or the minimum
                            amount permitted by law.
                        </p>
                        <p>
                            By using WAIVERA, you expressly waive any claims against Waivera, Inc. for damages,
                            losses, or liabilities arising from your use of the platform, except as explicitly stated
                            in these Terms.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: "termination",
            title: "Termination",
            content: (
                <>
                    <div className="text-gray-700 space-y-4 text-sm sm:text-base">
                        <h3 className="font-bold text-gray-800 text-lg">12.1 Right to Suspend or Terminate Access:</h3>
                        <p>
                            Waivera, Inc. reserves the right to suspend
                            or terminate your access to WAIVERA at any time, with or without prior notice, if
                            you:
                            <ul className="list-disc">
                                <li>Violate any provisions of these Terms of Service or any applicable laws and
                                    regulations.</li>
                                <li>Engage in unauthorized, fraudulent, or illegal activities on the platform.</li>
                                <li>Attempt to compromise the security, integrity, or functionality of WAIVERA.</li>
                                <li>Provide false, misleading, or incomplete information during account registration
                                    or use.</li>
                                <li>Fail to comply with HIPAA, Medicaid regulations, or state-specific laws governing
                                    electronic signatures and data protection.</li>
                            </ul>
                            Termination of access may also occur if Waivera, Inc. determines that continued use of
                            the platform poses a security risk, legal liability, or operational burden to the
                            company or other users.
                        </p>
                        <h3 className="font-bold text-gray-800 text-lg">12.2 Process for Account Termination:</h3>
                        <p>
                            Users who wish to voluntarily terminate their
                            WAIVERA account may do so by:
                            <ul className="list-disc">
                                <li>Submitting a written request to <a href="mailto:notifications@waivera.com" className="text-blue-600 hover:underline">notifications@waivera.com</a> from the registered email
                                    address associated with their account.</li>
                                <li>Confirming identity verification, as required by Waivera, Inc., to prevent
                                    unauthorized terminations.</li>
                            </ul>
                            Once an account is terminated:
                            <ul className="list-disc">
                                <li>User access will be revoked, and all login credentials will be disabled.</li>
                                <li>Any stored documents, electronic signature records, and associated data may be
                                    retained in accordance with legal, regulatory, and compliance requirements
                                    (e.g., HIPAA data retention policies).</li>
                                <li>Waivera, Inc. is not responsible for recovering or restoring deleted user data after
                                    account termination.</li>
                            </ul>
                        </p>
                        <p>
                            If your account has been terminated for a violation of these Terms, you may not re-
                            register or attempt to access WAIVERA without prior written approval from Waivera, Inc.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: "modificationsTerms",
            title: "Modifications to Terms",
            content: (
                <>
                    <div className="text-gray-700 space-y-4 text-sm sm:text-base">
                        <p>
                            Waivera, Inc. reserves the right to update, modify, or revise these Terms of Service at
                            any time, at its sole discretion. Any changes will take effect immediately upon being
                            published on WAIVERA or communicated to users through official channels, such as
                            email notifications or in-app alerts.
                        </p>
                        <p>
                            By continuing to use WAIVERA after any modifications to the Terms, you
                            acknowledge and accept the updated terms. If you do not agree to the revised
                            Terms, you must immediately discontinue use of the platform and, if necessary,
                            request account termination as outlined in Section 12 (Termination).
                        </p>
                        <p>
                            Waivera, Inc. encourages users to periodically review the Terms of Service to stay
                            informed about any updates. Your continued access and use of WAIVERA indicate
                            your ongoing agreement to comply with the most current version of these Terms.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: "contactInformation",
            title: "Contact Information",
            content: (
                <>
                    <div className="text-gray-700 space-y-4 text-sm sm:text-base">
                        <p>
                            If you have any questions, concerns, or inquiries regarding these Terms of Service,
                            WAIVERA’s functionality, or compliance matters, you may contact us through the
                            following channels:
                        </p>
                        <ul className="list-disc space-y-2">
                            <li><strong>Email:</strong> <a href="mailto:notifications@waivera.com" className="text-blue-600 hover:underline">notifications@waivera.com</a></li>
                            <li><strong>Mailing Address:</strong> Waivera, Inc. 10201 Park Rowe Avenue, Unit 2324, Baton Rouge, LA 70810</li>
                        </ul>
                        <p>
                            Waivera, Inc. values user feedback and strives to provide timely support regarding any
                            issues related to WAIVERA. Please allow a reasonable time for responses to inquiries,
                            especially those related to regulatory compliance and account-related matters.
                        </p>
                    </div>
                </>
            ),
        },
    ],
};
